import { useState, useEffect, useCallback } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

// Real Google reviews — Vacations Pro International Private Limited
// All verified from the Google Business profile (late 2024 – early 2026)
const REVIEWS = [
  {
    name: "Mandeep Saddowal",
    date: "Jan 2026",
    rating: 5,
    text: "Excellent service from start to finish. Vacations Pro organised a flawless 6-day itinerary for us. Great communication, reliable drivers, and beautiful hotel picks. If you're heading to Bali, definitely book with them!",
  },
  {
    name: "Gurman Sodhi",
    date: "Jan 2026",
    rating: 5,
    text: "Best services and great handling ever! Loved the curated package of Singapore. Never needed to make any query because everything was explained in the itinerary.",
  },
  {
    name: "Raj Kapil",
    date: "Jan 2026",
    rating: 5,
    text: "The trip was very nicely organised. The hotels booked through this agency were amazing. The car arranged was very comfortable and the driver very friendly and professional. Overall, we enjoyed the trip and will strongly recommend this company to our friends and relatives.",
  },
  {
    name: "Gaurav Jamwal",
    date: "Dec 2025",
    rating: 5,
    text: "Just wanted to let you know that the trip was fantastic and everything worked out seamlessly! The hotels and the tours were exactly as described — a perfect Goa break.",
  },
  {
    name: "Kishore Mukherjee",
    date: "Oct 2025",
    rating: 5,
    text: "Very professionally arranged four-day Kerala trip. All hotels booked were as promised. Taxi service was excellent. Looking forward to more trips like this with Vacations Pro in the future.",
  },
  {
    name: "SK",
    date: "Jun 2024",
    rating: 5,
    text: "Vacations Pro International is excellent! They offer reasonably priced packages with great recommendations. They also consider food restrictions.",
  },
  {
    name: "Kareena Sharma",
    date: "Jan 2024",
    rating: 5,
    text: "Efficient, quick, hassle-free service and truly great recommendations. The team made our family trip absolutely memorable.",
  },
];

export default function Testimonials() {
  const [i, setI] = useState(0);

  const next = useCallback(() => setI((p) => (p + 1) % REVIEWS.length), []);
  const prev = () => setI((p) => (p - 1 + REVIEWS.length) % REVIEWS.length);

  useEffect(() => {
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [next]);

  const r = REVIEWS[i];

  return (
    <section
      id="testimonials"
      className="bg-white py-20 md:py-24 px-6 md:px-12"
      data-testid="testimonials-section"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <span className="vp-label block mb-3">VERIFIED REVIEWS</span>
          <h2 className="font-serif-vp text-3xl md:text-4xl lg:text-5xl text-[var(--vp-primary)] leading-tight mb-6">
            <span className="text-[var(--vp-secondary)]">4.8</span> on Google · 264+ reviews.
            <br />Many journeys, many stories.
          </h2>

          {/* Review source — all 7 testimonials sourced from Google */}
          <div className="flex items-center justify-center gap-6 flex-wrap" data-testid="review-sources">
            <div className="flex items-center gap-2 bg-white border border-[var(--vp-border)] rounded-full pl-2 pr-4 py-1.5 shadow-sm">
              <GoogleGlyph />
              <div className="leading-tight text-left">
                <div className="flex items-center gap-1 text-xs font-medium text-[var(--vp-primary)]">
                  <span>4.8</span>
                  <Star className="w-3 h-3 fill-[#f5b014] text-[#f5b014]" />
                </div>
                <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)]">Verified on Google</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative bg-[var(--vp-bg)] rounded-2xl p-8 md:p-14 shadow-sm border border-[var(--vp-border)]" data-testid="testimonial-card">
          <Quote className="w-10 h-10 text-[var(--vp-accent)] mb-6" strokeWidth={1.25} />
          <blockquote className="font-serif-vp text-xl md:text-2xl lg:text-3xl text-[var(--vp-primary)] leading-[1.3] mb-8 italic">
            “{r.text}”
          </blockquote>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="font-medium text-[var(--vp-primary)]" data-testid="testimonial-name">{r.name}</div>
              <div className="text-xs text-[var(--vp-muted)] tracking-wider uppercase">{r.date}</div>
            </div>
            <div className="flex items-center gap-1">
              {Array.from({ length: r.rating }).map((_, idx) => (
                <Star key={idx} className="w-4 h-4 fill-[#f5b014] text-[#f5b014]" />
              ))}
            </div>
          </div>

          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous review"
              className="w-10 h-10 rounded-full bg-white border border-[var(--vp-border)] hover:bg-[var(--vp-primary)] hover:text-white hover:border-[var(--vp-primary)] transition-all flex items-center justify-center shadow-sm"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1.5">
              {REVIEWS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  aria-label={`Review ${idx + 1}`}
                  className={`h-[3px] transition-all rounded-full ${
                    idx === i ? "w-8 bg-[var(--vp-primary)]" : "w-3 bg-[var(--vp-border)]"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              aria-label="Next review"
              className="w-10 h-10 rounded-full bg-white border border-[var(--vp-border)] hover:bg-[var(--vp-primary)] hover:text-white hover:border-[var(--vp-primary)] transition-all flex items-center justify-center shadow-sm"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// Google "G" — official 4-color glyph
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" className="w-7 h-7" aria-label="Google">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
